import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import Button from "shared/Button";
import { useSessionStorage } from "shared/@hooks/useSessionStorage";
import { QUERY_KEYS } from "shared/@utils/queryKeys";
import moment from "moment-timezone";
import classNames from "classnames";

import styles from "./EditLOSV2EventPage.module.css";
import DeleteModal from "Events/Components/DeleteModal";
import UndoChangesModal from "Events/Components/UndoChangesModal";
import { EventPublishModal } from "Events/Components/EventPublishModal/EventPublishModal";
import { useEditEventsPage } from "./useEditEventPage";
import { useCurrentRoute } from "shared/@hooks/useCurrentRoute";
import Loader from "shared/Loader";
import _ from "lodash";
import EventTypeV2Card from "Events/V2Components/EventTypeV2Card";
import EventV2InfoForm from "Events/V2Components/EventV2InfoForm";
import PublishingV2InfoForm from "Events/V2Components/PublishingV2InfoForm";
import VenueV2Form from "Events/V2Components/VenueV2Form";
import TicketingV2Form from "Events/V2Components/TicketingV2Form";
import NotesV2Form from "Events/V2Components/NotesV2Form";
import { removeExtraLineSpaces } from "shared/@utils/removeExtraNewLines";
import formatFieldName from "shared/@utils/formatFieldName";
import ADAV2Form from "Events/V2Components/ADAV2Form";
import VirtualV2Form from "Events/V2Components/VirtualV2Form";
import CircumstancesV2Form from "Events/V2Components/CircumstancesV2Form";
import SessionsV2Form from "Events/V2Components/SessionsV2Form";
import ErrorModal from "../../Components/ErrorModal/ErrorModal";
import HostsV2Form from "Events/V2Components/HostsV2Form";
import { Modal } from "shared/Modal";

const timezoneLabelToIana = {
  "Pacific Time (PT)": "America/Los_Angeles",
  "Mountain Time (MT)": "America/Boise",
  "Central Time (CT)": "America/Chicago",
  "Eastern Time (ET)": "America/New_York",
  "Alaska Time (AKT)": "America/Anchorage",
  "Hawaii-Aleutian Time (HAT)": "America/Adak",
  "Hawaii-Honolulu (HST)": "Pacific/Honolulu",
  "Australian Eastern Standard Time (AEST)": "Australia/Sydney",
  "Australian Central Standard Time (ACST)": "Australia/Adelaide",
  "Australian Western Standard Time (AWST)": "Australia/Perth",
  "New Zealand Standard Time (NZST)": "Pacific/Auckland",
  "Australian Eastern Daylight Time (AEDT)": "Australia/Sydney",
  "New Zealand Daylight Time (NZDT)": "Pacific/Auckland",
};

const toIanaTimeZone = (timeZone) =>
  timezoneLabelToIana[timeZone] || timeZone || "America/Los_Angeles";

export function EditLOSV2EventPage() {
  const history = useHistory();
  const queryClient = useQueryClient();

  const { eventId } = useParams();
  const {
    data: eventData,
    isLoading,
    eventEditPut,
    eventEditPutSilent,
    eventPublish,
    refetch,
    errorModal,
    closeErrorModal,
    isPublishing,
    isEditLoading,
  } = useEditEventsPage({ eventId });

  const { data: states } = useCurrentRoute();

  // const [eventData, setEventData] = useState(null);
  const [event, setEvent] = useState(null);

  const [loadingPublish, setLoadingPublish] = useState(false);
  const [showSavedModal, changeShowSavedModal] = useState(false);
  const [showRemoveModal, changeShowRemoveModal] = useState(false);
  const [undoChangesModal, setUndoChangesModal] = useState(false);
  const [removeChangesModal, setRemoveChangesModal] = useState(false);
  const [showUndoButton, setShowUndoButton] = useState(false);
  const [isMissingFieldsmodalOpen, setIsMissingFieldsModalOpen] =
    useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [enablePublishButton, setEnablePublishButton] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [undoPath, setUndoPath] = useState("");
  const [isFormValid, setIsFormValid] = useState(true);
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [loadingUndo, setLoadingUndo] = useState(false);

  const [eventInfo, setEventInfo] = useState({
    event: {
      name: "",
      ruleset: "",
      location: "",
      jobCode: "",
      dates: {},
      country: "",
      city: "",
      state: "",
      status: "",
      notes: {},
      securityNotes: "",
      financial: {},
    },
    marketing: {
      alertNotes: {},
      title: "",
      description: "",
      infoNotes: {},
      tentative: "",
      active: "",
      primaryCaption: "",
      secondaryCaption: "",
      visibleCalendar: "",
      visibleTo: "",
      notes: {},
    },
    virtual: {
      eventLink: {},
      eventType: {},
      linkedEvent: { eventId: {}, name: {}, id: {} },
    },
    analytics: {
      sales: {
        soldOut: "",
        capacity: {
          purchase: 0,
          platinum: 0,
        },
      },
      countingTickets: "",
    },
    venue: {
      company: {},
      room: {},
    },
    sessions: [
      {
        type: "",
        sessionName: "",
        description: "",
        speakers: [],
        location: "",
        capacity: "",
        startDate: "",
        endDate: "",
        // checkInStartDate: "",
        // checkInEndDate: "",
        // doorsOpenStartDate: "",
        // doorsOpenEndDate: "",
      },
    ],
    hosts: [],
    ada: [
      {
        component: {},
        eventAdaId: {},
        icon: {},
        id: "",
        order: {},
        param1: {},
        param2: {},
      },
    ],
    tickets: [],
    speakers: [],
    visibleTo: [],
  });

  useEffect(() => {
    if (eventData) {
      setEvent(eventData);

      setEventInfo((prevState) => ({
        ...prevState,
        event: {
          name: eventData.event?.name,
          ruleset: eventData.event?.ruleset,
          location: eventData.event?.location,
          jobCode: eventData.event?.jobCode,
          city: eventData.event?.city || "",
          state: eventData.event?.state || "",
          status: eventData.event?.status || "",
          country: eventData.event?.country || "",
          dates: eventData.event?.dates || "",
          notes: eventData.event?.notes || {
            saved: "",
            value: "",
            permission: "hidden",
          },
          securityNotes: eventData.event?.securityNotes || "",
          financial: eventData.event?.financial || {
            cartType: {
              saved: { cartTypeId: "", name: "" },
              value: { cartTypeId: "", name: "" },
            },
          },
        },
        tickets: eventData.tickets || [],
        marketing: {
          alertNotes: eventData.marketing?.alertNotes || {
            saved: "",
            value: "",
            permission: "hidden",
          },
          title: eventData.marketing?.title || "",
          description: eventData.marketing?.description || "",
          infoNotes: eventData.marketing?.infoNotes || {
            saved: "",
            value: "",
            permission: "hidden",
          },
          tentative: eventData.marketing?.tentative || false,
          active: eventData.marketing?.active || false,
          primaryCaption: eventData.marketing?.primaryCaption || "",
          secondaryCaption: eventData.marketing?.secondaryCaption || "",
          visibleCalendar: eventData.marketing?.visibleCalendar || false,
          visibleTo: eventData.marketing?.visibleTo || "",
          notes: eventData.marketing?.notes || {
            saved: "",
            value: "",
            permission: "hidden",
          },
        },
        virtual: {
          eventLink: eventData.virtual?.eventLink || "",
          eventType: eventData.virtual?.eventType || "",
          linkedEvent: eventData.virtual?.linkedEvent || {
            eventId: "",
            name: "",
            id: "",
          },
        },
        analytics: {
          countingTickets: eventData.analytics?.countingTickets || false,
          sales: {
            soldOut: eventData.analytics?.sales?.soldOut || false,
            capacity: {
              purchase: eventData.analytics?.sales?.capacity?.purchase || 0,
              platinum: eventData.analytics?.sales?.capacity?.platinum || 0,
            },
          },
        },
        venue: {
          company: eventData.venue.company || {},
          room: {
            saved: eventData.venue?.room?.saved || {},
            value: eventData.venue?.room?.value || {},
          },
        },
        sessions: eventData.sessions || [],
        ada:
          eventData.ada?.map((entry) => ({
            component: entry.component || "",
            eventAdaId: entry.eventAdaId || "",
            icon: entry.icon || "",
            id: entry.id || "",
            order: entry.order || "",
            param1: entry.param1 || "",
            param2: entry.param2 || "",
          })) || [],
        speakers: eventData.speakers,
        visibleTo: eventData.visibleTo,
        hosts: eventData.hosts || [],
      }));
    }
    setRemoveChangesModal(false);
  }, [eventData]);

  // state to hold form data
  const [formData, setFormData] = useSessionStorage("formData", {
    venue: {
      state: "",
      city: "",
      venue: "",
      noVenue: false,
    },
  });

  // Check if saved value exists, excluding event.notes.saved from the check
  const checkSavedValues = (obj, parentKey = "") => {
    for (let key in obj) {
      // Skip checking for event.notes.saved and event.country.saved
      if (parentKey === "event" && key === "notes") continue;

      if (parentKey === "venue" && (key === "company" || key === "room")) {
        const saved = obj[key].saved || {};
        const value = obj[key].value || {};
        for (let prop in saved) {
          if (saved[prop] !== null && saved[prop] !== value[prop]) {
            return true;
          }
        }
        continue;
      }

      if (typeof obj[key] === "object" && obj[key] !== null) {
        const currentPath = parentKey ? `${parentKey}.${key}` : key;

        // if (
        //   parentKey.startsWith("sessions") &&
        //   obj[key].visibility === "Limit To Groups"
        // ) {
        //   const visibleTo = obj[key].visibleTo || [];
        //   if (Array.isArray(visibleTo) && visibleTo.length === 0) {
        //     return true;
        //   }
        // }

        if (obj[key].hasOwnProperty("saved") && obj[key].saved) {
          if (obj[key].hasOwnProperty("value")) {
            const saved = obj[key].saved;
            const value = obj[key].value;

            // If both saved and value are arrays, compare their lengths and contents
            if (Array.isArray(saved) && Array.isArray(value)) {
              if (
                saved.length === value.length &&
                saved.every((item, index) => item === value[index])
              ) {
                continue;
              }
            } else if (saved === value) {
              continue;
            }

            return true;
          }
        }

        if (checkSavedValues(obj[key], currentPath)) {
          return true;
        }
      }
    }
    return false;
  };

  const validateFields = (data) => {
    let invalidFields = [];
    let isValid = true;

    const checkField = (field) => {
      if (!field || typeof field !== "object") {
        return;
      }

      const skipFields = ["Event Information > Job Code"];

      if (skipFields.includes(field.field)) {
        return;
      }

      // Check if the field is required but both saved and value are missing or invalid
      const isEmpty = (val) => val === null || val === undefined || val === "";

      if (field.required && isEmpty(field.saved) && isEmpty(field.value)) {
        const isAdaField = field?.field === "ADA > Description";
        if (!isAdaField || (isAdaField && data.ada?.length === 1)) {
          //console.log(`  ❌ Field "${field}" is INVALID.`);
          invalidFields.push(field.field || "Unnamed Field");
          isValid = false;
        }
      }
    };
    if (
      (data.marketing?.visibleTo?.saved || data.marketing?.visibleTo?.value) ===
        "Limit To Groups" &&
      Array.isArray(data.visibleTo?.saved) &&
      data.visibleTo?.saved.length === 0
    ) {
      invalidFields.push(data.visibleTo?.field);
      isValid = false;
    }

    // if (data.ada?.length === 1 && data.ada[0]?.param1?.saved !== null) {
    //   invalidFields.push("Must Have Component in ADA");
    //   isValid = false;
    // }

    const traverseData = (data) => {
      for (const key in data) {
        const field = data[key];

        if (Array.isArray(field)) {
          // require sessions to exist and not be empty
          if (key === "sessions") {
            const location = data?.event?.location?.value;
            const isOnsite = location === "onsite";

            if (isOnsite && (!Array.isArray(field) || field.length === 0)) {
              invalidFields.push("Sessions");
              isValid = false;
            }

            field.forEach((session) => {
              if (
                (session.visibility?.saved || session.visibility?.value) ===
                  "Limit To Groups" &&
                Array.isArray(session.visibleTo?.saved) &&
                session.visibleTo?.saved.length === 0
              ) {
                invalidFields.push(session.visibleTo?.field);
                isValid = false;
              }
            });
          }

          // Validate non-empty arrays
          if (field.length > 0) {
            field.forEach((item) => traverseData(item));
          }
        } else if (typeof field === "object" && field !== null) {
          if (field.required === true && typeof field.field === "string") {
            checkField(field);
          } else {
            traverseData(field);
          }
        }
      }
    };

    traverseData(data);
    setMissingFields(invalidFields);
    return isValid;
  };

  useEffect(() => {
    if (errorModal.isOpen) {
      changeShowSavedModal(false);
    }
  }, [errorModal.isOpen]);

  useEffect(() => {
    // Always recalculate unsavedChangesExist and isFormValid
    const isFormValid = validateFields(eventInfo);
    const unsavedChangesExist = checkSavedValues(eventInfo);

    // Update button states based on unsaved changes and validation results
    const shouldEnablePublish =
      isFormValid &&
      (unsavedChangesExist ||
        eventInfo?.event?.status?.value === "Unpublished" ||
        eventInfo?.event?.status?.value !== "Publishing");

    setHasUnsavedChanges(unsavedChangesExist);
    setShowUndoButton(unsavedChangesExist);
    setEnablePublishButton(shouldEnablePublish);
  }, [JSON.stringify(eventInfo)]);

  const debouncedHandleTicketingChangeRef = useRef(
    _.debounce(async (newTicketData, isAddingNew, callback) => {
      try {
        if (isAddingNew) {
          await eventEditPut.mutateAsync({ tickets: newTicketData });
        } else {
          await eventEditPutSilent.mutateAsync({ tickets: newTicketData });
        }
        if (callback) callback(); // Execute callback after mutation completes
      } catch (error) {
        console.error("Backend update failed:", error);
      }
    }, 800)
  );

  const handleTicketingForm = (newTicketData, isAddingNew, removalSet) => {
    // console.log("Incoming Ticket Data:", newTicketData);
    const copiedTicketData = JSON.parse(JSON.stringify(newTicketData));

    setEventInfo((prev) => {
      const updatedInfo = {
        ...prev,
        tickets: copiedTicketData,
      };

      const processTicketData = (data, prevData = {}) => {
        return data.map((field) => {
          const existingField = prevData[field.id] || {};

          Object.keys(field).forEach((key) => {
            if (
              typeof field[key] === "object" &&
              field[key] !== null &&
              field[key].hasOwnProperty("saved")
            ) {
              const prevField = existingField[key] || {};

              field[key] = {
                ...prevField,
                ...field[key],
                saved:
                  field[key].saved !== null
                    ? field[key].saved
                    : field[key].value,
              };
            }
          });

          if (field.tickets) {
            field.tickets = processTicketData(
              field.tickets,
              existingField.tickets || []
            );
          }

          return field;
        });
      };

      const formatForBackend = (data) => {
        return data.map((field) => {
          const formattedField = {};

          Object.keys(field).forEach((key) => {
            if (
              typeof field[key] === "object" &&
              field[key] !== null &&
              field[key].hasOwnProperty("saved")
            ) {
              formattedField[key] =
                field[key].saved !== null ? field[key].saved : field[key].value;
            } else {
              formattedField[key] = field[key];
            }
          });

          if (field.tickets) {
            formattedField.tickets = formatForBackend(field.tickets);
          }

          return formattedField;
        });
      };

      const updatedTicketData = processTicketData(
        newTicketData,
        Object.fromEntries(prev.tickets.map((ticket) => [ticket.id, ticket]))
      );

      const backendFormattedData = formatForBackend(updatedTicketData);

      debouncedHandleTicketingChangeRef.current(
        backendFormattedData,
        isAddingNew,
        () => {
          if (removalSet) {
            handleClearSessionsTicketSet(
              removalSet.saved || removalSet.value || removalSet
            );
          }
        }
      );

      return updatedInfo;
    });
  };

  const handleClearSessionsTicketSet = (ticketSet) => {
    const updatedSessions = eventInfo.sessions.map((session) => {
      // Start with a copy to preserve all fields
      const transformedSession = { ...session };

      for (const key in session) {
        const field = session[key];

        if (typeof field === "object" && field !== null) {
          if (key === "visibleTo") {
            let newVisibleTo = [];

            if (Array.isArray(field)) {
              // If 'visibleTo' is already a plain array, filter it directly
              newVisibleTo = field.filter((set) => set.ticketSet !== ticketSet);
            } else if (
              field.hasOwnProperty("saved") &&
              field.hasOwnProperty("value")
            ) {
              // If 'visibleTo' has 'saved' and 'value'
              const newSaved = Array.isArray(field.saved)
                ? field.saved.filter((set) => set.ticketSet !== ticketSet)
                : [];

              // Determine the correct array to use
              newVisibleTo = newSaved.length > 0 ? newSaved : field.value;
            }

            // Ensure it's always an array (fallback to empty array if needed)
            transformedSession[key] = Array.isArray(newVisibleTo)
              ? newVisibleTo
              : [];
          } else if (
            field.hasOwnProperty("saved") &&
            field.hasOwnProperty("value")
          ) {
            // For other fields with saved/value structure, prioritize saved unless it's empty/null, then fallback to value
            transformedSession[key] =
              field.saved?.length > 0 ? field.saved : field.value;
          }
        }
      }

      return transformedSession;
    });

    handleSessions(updatedSessions, true);
  };

  const handleEventTypeLocation = useCallback((selectedLocation) => {
    const newLocation =
      typeof selectedLocation === "string"
        ? selectedLocation.trim().toLowerCase()
        : "";

    setEventInfo((prevData) => {
      if (prevData.event.location.saved === newLocation) return prevData;

      const updateLocation = { event: { location: newLocation } };
      eventEditPut.mutateAsync(updateLocation);

      return {
        ...prevData,
        event: {
          ...prevData.event,
          location: { ...prevData.event.location, saved: newLocation },
        },
      };
    });
  }, []);

  const [undoIndex, setUndoIndex] = useState(null);
  const [undoField, setUndoField] = useState("");
  const [undoFieldDisplay, setUndoFieldDisplay] = useState("");
  const [original, setOriginal] = useState("");

  //Handles event info form
  const handleMarketingOnBlur = (obj, key, saved) => {
    if (saved === null) return;

    setEventInfo((prevData) => {
      const currentSavedValue = _.get(prevData, `${obj}.${key}.saved`, null);

      // Early exit if there is no change
      if (_.isEqual(currentSavedValue, saved)) {
        return prevData;
      }

      // Update state
      const updatedData = _.set(
        _.cloneDeep(prevData),
        `${obj}.${key}.saved`,
        saved
      );

      // Prepare payload for backend call
      const payload = { [obj]: { [key]: saved || null } };

      // Trigger the debounced backend call
      debouncedHandleMarketingChangeRef.current(obj, key, payload);

      return updatedData;
    });
  };

  const debouncedHandleMarketingChangeRef = useRef(
    _.debounce((obj, key, payload) => {
      eventEditPutSilent.mutateAsync(payload).catch((error) => {
        console.error(`Failed to update ${obj}.${key}`, error);
      });
    }, 300)
  );

  //Deeply nested object
  const constructNestedUpdate = (path, value) => {
    const keys = path.split(".");
    const result = {};
    let current = result;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        current[key] = current[key] || {};
        current = current[key];
      }
    });

    return result;
  };

  //Publish info
  const handlePublishChange = useCallback(
    ({ path, value }) => {
      // Create a new promise that will be returned
      return new Promise((resolve, reject) => {
        const debouncedUpdate = _.debounce(() => {
          const newValue = value || null;
          const update = constructNestedUpdate(path, newValue);

          setEventInfo((prevEventInfo) =>
            _.merge(_.cloneDeep(prevEventInfo), update)
          );

          eventEditPut.mutateAsync(update, {
            onSuccess: () => {
              const significantPaths = [
                "analytics.sales.soldOut",
                "analytics.sales.capacity.purchase",
                "analytics.sales.capacity.platinum",
                "analytics.countingTickets",
                "marketing.primaryCaption",
                "marketing.secondaryCaption",
                "marketing.visibleCalendar",
                "marketing.visibleTo",
                "marketing.tentative",
                "marketing.active",
              ];
              const shouldInvalidate = significantPaths.some(
                (significantPath) => path.startsWith(significantPath)
              );

              if (shouldInvalidate) {
                queryClient.invalidateQueries(["event", eventId]);
              }
              resolve();
            },
            onError: (err) => {
              console.error("Update failed:", err);
              reject(err);
            },
          });
        }, 300);

        debouncedUpdate();
      });
    },
    [queryClient, eventId, eventEditPut]
  );

  const handleSpeakerChange = async (newSpeakers) => {
    await eventEditPut.mutateAsync({
      speakers: newSpeakers,
    });
  };

  const eventInfoRef = useRef(eventInfo);

  useEffect(() => {
    eventInfoRef.current = eventInfo;
  }, [eventInfo]);

  //LinkedVirtualEvent
  const handleVirtualEventChange = async (
    newVirtualId,
    newVirtualEventId,
    newVirtualEventName
  ) => {
    console.log(eventInfoRef);
    const formatForBackend = (data) => {
      return data.map((field) => {
        const formattedField = {};

        Object.keys(field).forEach((key) => {
          if (
            typeof field[key] === "object" &&
            field[key] !== null &&
            field[key].hasOwnProperty("saved")
          ) {
            formattedField[key] =
              field[key].saved !== null ? field[key].saved : field[key].value;
          } else {
            formattedField[key] = field[key];
          }
        });

        if (field.tickets) {
          formattedField.tickets = formatForBackend(field.tickets);
        }

        return formattedField;
      });
    };
    try {
      const payload = {
        tickets: formatForBackend(
          eventInfoRef.current.tickets.map((ticketSet) => ({
            ...ticketSet,
            tickets: ticketSet.tickets.map((ticket) => ({
              ...ticket,
              linkedVirtualTicket: {
                ...ticket.linkedVirtualTicket,
                saved: {
                  ticketId: null,
                  name: null,
                },
              },
            })),
          }))
        ),
        virtual: {
          linkedEvent: {
            eventId: newVirtualEventId,
            name: newVirtualEventName,
            id: newVirtualId,
          },
        },
      };

      await eventEditPut.mutateAsync(payload);

      setEventInfo((prevEventInfo) => ({
        ...prevEventInfo,
        //Tickets
        tickets: prevEventInfo.tickets.map((ticketSet) => ({
          ...ticketSet,
          tickets: ticketSet.tickets.map((ticket) => ({
            ...ticket,
            linkedVirtualTicket: {
              ...ticket.linkedVirtualTicket,
              saved: {
                ticketId: null,
                name: null,
              },
            },
          })),
        })),

        //Event
        virtual: {
          ...prevEventInfo.virtual,
          linkedEvent: {
            ...prevEventInfo.virtual.linkedEvent,
            saved: {
              ...prevEventInfo.virtual.linkedEvent.saved,
              eventId: newVirtualEventId,
              name: newVirtualEventName,
              id: newVirtualId,
            },
          },
        },
      }));
    } catch (error) {
      console.error("Error updating virtual event:", error);
    }
  };

  //CartType
  const handleCartTypeChange = async (name, cartTypeId) => {
    try {
      const payload = {
        event: {
          financial: {
            cartType: {
              cartTypeId: cartTypeId,
              name: name,
            },
          },
        },
      };
      await eventEditPut.mutateAsync(payload);

      setEventInfo((prevEventInfo) => ({
        ...prevEventInfo,
        event: {
          ...prevEventInfo.event,
          financial: {
            ...prevEventInfo.event.financial,
            cartType: {
              ...prevEventInfo.event.financial.cartType,
              name: name,
              cartTypeId: cartTypeId,
            },
          },
        },
      }));
    } catch (error) {
      console.error("Error updating cart type:", error);
    }
  };

  const handleGroupChange = async (newGroupsArray) => {
    await eventEditPut.mutateAsync({
      visibleTo: newGroupsArray,
    });
  };

  const handleCaptionsChange = (dataType, newValue) => {
    newValue = newValue.replace(/↵/g, "\r\n").trim();
    newValue = newValue === "" ? null : newValue;

    setEventInfo((prev) => {
      // Determine current saved value based on dataType
      let currentSavedValue = "";
      if (dataType === "marketing.primary") {
        currentSavedValue = prev.marketing?.primaryCaption?.saved?.trim() || "";
      } else if (dataType === "marketing.secondary") {
        currentSavedValue =
          prev.marketing?.secondaryCaption?.saved?.trim() || "";
      }

      // Early exit if there's no change
      if (newValue === currentSavedValue) {
        return prev;
      }

      const updatedInfo = {
        ...prev,
        marketing: {
          ...prev.marketing,
          ...(dataType === "marketing.primary" && {
            primaryCaption: {
              ...prev.marketing.primaryCaption,
              saved: newValue,
            },
          }),
          ...(dataType === "marketing.secondary" && {
            secondaryCaption: {
              ...prev.marketing.secondaryCaption,
              saved: newValue,
            },
          }),
        },
      };

      const captionsPayload =
        dataType === "marketing.primary"
          ? { marketing: { primaryCaption: newValue } }
          : { marketing: { secondaryCaption: newValue } };

      debouncedHandleCaptionsChangeRef.current(dataType, captionsPayload);
      return updatedInfo;
    });
  };

  const debouncedHandleCaptionsChangeRef = useRef(
    _.debounce((dataType, newCaptions) => {
      eventEditPutSilent.mutateAsync(newCaptions);
    }, 300)
  );

  const processSessionData = (session) => {
    return Object.fromEntries(
      Object.entries(session).map(([key, details]) => {
        if (
          details &&
          typeof details === "object" &&
          "saved" in details &&
          "value" in details
        ) {
          const savedIsEmptyArray =
            Array.isArray(details.saved) && details.saved.length === 0;
          const valueIsEmptyArray =
            Array.isArray(details.value) && details.value.length === 0;

          if (!savedIsEmptyArray && details.saved !== null) {
            return [key, details.saved];
          } else if (!valueIsEmptyArray) {
            return [key, details.value];
          } else {
            return [key, null];
          }
        }
        return [key, details];
      })
    );
  };

  const handleUndoSessions = async () => {
    const sessionIndex = parseInt(undoPath.split(".")[1], 10);
    const propToUpdate = undoPath.split(".")[2];
    const sessionsArray = eventInfo.sessions || [];

    if (sessionIndex >= 0 && sessionIndex < sessionsArray.length) {
      const sessionToUpdate = sessionsArray[sessionIndex];

      // Process the session to ensure flat structure
      const processedSession = processSessionData(sessionToUpdate);

      let updatedSessions;

      if (propToUpdate === "speakers") {
        // Update speakers array to be empty
        updatedSessions = sessionsArray.map((session, index) =>
          index === sessionIndex
            ? { ...processedSession, speakers: null }
            : processSessionData(session)
        );
      } else if (propToUpdate === "visibleTo") {
        // Update speakers array to be empty
        updatedSessions = sessionsArray.map((session, index) =>
          index === sessionIndex
            ? { ...processedSession, visibleTo: null }
            : processSessionData(session)
        );
      } else {
        // Update specific property to null
        updatedSessions = sessionsArray.map((session, index) =>
          index === sessionIndex
            ? { ...processedSession, [propToUpdate]: null }
            : processSessionData(session)
        );
      }

      // console.log("Updated Sessions:", updatedSessions, undoPath);
      await eventEditPut.mutateAsync({ sessions: updatedSessions });
    } else {
      console.error("Invalid session index:", sessionIndex);
    }
  };

  const handleUndoTickets = async () => {
    // console.log("undoPath", undoPath);

    const ticketSetId = undoPath.split(".")[1];
    const addRemoveBadgesOrTickets = undoPath.split(".")[2];
    const ticketId = undoPath.split(".")[3];
    const ticketPropToUpdate = undoPath.split(".")[4];
    const ticketingArray = transformData(eventInfo.tickets).savedArray;
    const originalTicketingArray = transformData(eventInfo.tickets).valueArray;

    let payload = null;
    // console.log("ticketingArray", ticketingArray);
    // console.log("originalTicketingArray", originalTicketingArray);

    if (addRemoveBadgesOrTickets == "add") {
      const updatedTicketArray = {
        tickets: [
          ...ticketingArray,
          originalTicketingArray.find((set) => set.id == ticketSetId),
        ],
      };

      payload = updatedTicketArray;
    } else if (addRemoveBadgesOrTickets == "remove") {
      const updatedTicketArray = {
        tickets: ticketingArray.filter((set) => set.id !== ticketSetId),
      };
      await eventEditPut.mutateAsync(updatedTicketArray);
    } else if (addRemoveBadgesOrTickets == "badges") {
      const updatedTicketArray = {
        tickets: ticketingArray.map((set) =>
          set.id === ticketSetId ? { ...set, badges: null } : set
        ),
      };

      payload = updatedTicketArray;
    } else if (
      addRemoveBadgesOrTickets == "title" ||
      addRemoveBadgesOrTickets == "subTitle"
    ) {
      const updatedTicketArray = {
        tickets: ticketingArray.map((set) =>
          set.id === ticketSetId
            ? { ...set, [addRemoveBadgesOrTickets]: null }
            : set
        ),
      };

      payload = updatedTicketArray;
    } else if (addRemoveBadgesOrTickets == "tickets") {
      if (ticketPropToUpdate == "originalTicket") {
        const updatedTicketArray = {
          tickets: ticketingArray.map((set) =>
            set.id === ticketSetId
              ? {
                  ...set,
                  tickets: [
                    ...set.tickets,
                    originalTicketingArray
                      .find((set) => set.id == ticketSetId)
                      .tickets.find((tkt) => tkt.id == ticketId),
                  ],
                }
              : set
          ),
        };

        payload = updatedTicketArray;
      } else if (ticketPropToUpdate == "ticket") {
        const updatedTicketArray = {
          tickets: ticketingArray.map((set) =>
            set.id === ticketSetId
              ? {
                  ...set,
                  tickets: set.tickets.filter((tkt) => tkt.id !== ticketId),
                }
              : set
          ),
        };

        payload = updatedTicketArray;
      } else if (ticketPropToUpdate == "linkedVirtualTicket") {
        const updatedTicketArray = {
          tickets: ticketingArray.map((set) => {
            if (set.id === ticketSetId) {
              return {
                ...set,
                tickets: set.tickets.map((tkt) => {
                  if (tkt.id === ticketId) {
                    const updatedTicket = {
                      ...tkt,
                      linkedVirtualTicket: {
                        ticketId: null,
                        name: null,
                      },
                    };
                    return updatedTicket;
                  }

                  return tkt;
                }),
              };
            }

            return set;
          }),
        };

        payload = updatedTicketArray;
      } else {
        const updatedTicketArray = {
          tickets: ticketingArray.map((set) => {
            if (set.id === ticketSetId) {
              return {
                ...set,
                tickets: set.tickets.map((tkt) => {
                  if (tkt.id === ticketId) {
                    const updatedTicket = {
                      ...tkt,
                      [ticketPropToUpdate]: null,
                    };
                    return updatedTicket;
                  }

                  return tkt;
                }),
              };
            }

            return set;
          }),
        };

        payload = updatedTicketArray;
      }
    }
    if (payload) {
      payload = payload.tickets.map((ticketSet) => ({
        ...ticketSet,
        // ticketSetType: originalTicketingArray.find(
        //   (set) => set.id == ticketSet.id
        // ).ticketSetType,
        tickets: ticketSet.tickets.map((ticket) => ({
          ...ticket,
          _id: ticket.id,
        })),
      }));

      await eventEditPut.mutateAsync({ tickets: payload });
    }
  };

  const handleUndoCartType = async () => {
    await eventEditPut.mutateAsync({
      event: { financial: { cartType: { name: null, cartTypeId: null } } },
    });
  };

  const handleUndoSpeakers = async () => {
    await eventEditPut.mutateAsync({ speakers: null });
  };
  const handleUndoHosts = async () => {
    await eventEditPut.mutateAsync({ hosts: null });
  };
  const handleUndoVisibleTo = async () => {
    await eventEditPut.mutateAsync({ visibleTo: null });
  };

  const handleUndoADA = async () => {
    const adaIndex = parseInt(undoPath.split(".")[1], 10);
    const adaField = undoPath.split(".")[2];

    // Update the specific ADA field in the state
    const updatedAda = eventInfo.ada.map((entry, index) => {
      if (index === adaIndex) {
        return {
          ...entry,
          [adaField]: {
            ...entry[adaField],
            saved: null,
          },
        };
      }
      return entry;
    });

    const transformedEntries = updatedAda.map((entry) => {
      const { id, component, icon, order, param1, param2 } = entry;

      return {
        _id: id,
        component: component?.saved || component?.value || null,
        icon: icon?.saved || icon?.value || null,
        order: order?.saved || order?.value || null,
        param1: param1?.saved || param1?.value || null,
        param2: param2?.saved || param2?.value || null,
      };
    });

    setEventInfo((prev) => ({
      ...prev,
      ada: updatedAda,
    }));

    await eventEditPut.mutateAsync({ ada: transformedEntries });
    setRemoveChangesModal(false);
  };

  const handleUndoVenue = async () => {
    await eventEditPut.mutateAsync({
      venue: {
        company: {
          city: null,
          country: null,
          email: null,
          fax: null,
          isVerified: null,
          latitude: null,
          longitude: null,
          netSuiteId: null,
          phone: null,
          state: null,
          stateId: null,
          street1: null,
          street2: null,
          url: null,
          venueId: null,
          venueName: null,
          zip: null,
          noVenue: null,
        },
        room: {
          roomId: null,
          name: null,
          capacity: null,
        },
      },
    });
  };

  //Dates
  const SESSION_DATE_FIELDS = [
    "startDate",
    "endDate",
    // "checkInStartDate",
    // "doorsOpenStartDate",
  ];

  const getSessionDateValue = (session, field) => {
    const fieldValue = session?.[field];

    if (
      fieldValue &&
      typeof fieldValue === "object" &&
      !Array.isArray(fieldValue) &&
      ("saved" in fieldValue || "value" in fieldValue)
    ) {
      return fieldValue.saved ?? fieldValue.value ?? null;
    }

    return fieldValue ?? null;
  };

  const rebaseUtcDateToNewTimezone = (utcDate, fromTimeZone, toTimeZone) => {
    if (!utcDate || typeof utcDate !== "string") return null;

    const fromIana = toIanaTimeZone(fromTimeZone);
    const toIana = toIanaTimeZone(toTimeZone);

    const local = moment.utc(utcDate).tz(fromIana);

    return moment
      .tz(
        {
          year: local.year(),
          month: local.month(),
          date: local.date(),
          hour: local.hour(),
          minute: local.minute(),
          second: local.second(),
          millisecond: local.millisecond(),
        },
        toIana
      )
      .utc()
      .toISOString();
  };

  const rebuildSessionsForTimezoneChange = (
    sessions = [],
    fromTimeZone,
    toTimeZone
  ) => {
    if (!fromTimeZone || !toTimeZone || fromTimeZone === toTimeZone) {
      return sessions;
    }

    return sessions.map((session) => {
      const updatedSession = { ...session };

      SESSION_DATE_FIELDS.forEach((field) => {
        const rawUtc = getSessionDateValue(session, field);

        if (!rawUtc) {
          return;
        }

        const rebuiltUtc = rebaseUtcDateToNewTimezone(
          rawUtc,
          fromTimeZone,
          toTimeZone
        );

        if (!rebuiltUtc) {
          return;
        }

        if (
          session?.[field] &&
          typeof session[field] === "object" &&
          !Array.isArray(session[field]) &&
          ("saved" in session[field] || "value" in session[field])
        ) {
          updatedSession[field] = {
            ...session[field],
            saved: rebuiltUtc,
          };
        } else {
          updatedSession[field] = rebuiltUtc;
        }
      });

      return updatedSession;
    });
  };

  const formatSessionsForBackend = (sessions = []) => {
    return sessions.map((session) => {
      const formattedSession = {};

      Object.keys(session).forEach((key) => {
        if (
          typeof session[key] === "object" &&
          session[key] !== null &&
          session[key].hasOwnProperty("saved")
        ) {
          formattedSession[key] =
            session[key].saved !== null
              ? session[key].saved
              : session[key].value;
        } else {
          formattedSession[key] = session[key];
        }
      });

      return formattedSession;
    });
  };

  const TICKET_DATE_TIMEZONE_FIELDS = {
    purchaseStartDate: "purchaseStartTimeZone",
    purchaseEndDate: "purchaseStartTimeZone",
    startFeeDate: "purchaseStartTimeZone",
    changeableUntilDate: "purchaseStartTimeZone",
  };

  const TICKET_DATE_FIELDS = Object.keys(TICKET_DATE_TIMEZONE_FIELDS);

  const getFieldValue = (field) => {
    if (
      field &&
      typeof field === "object" &&
      !Array.isArray(field) &&
      ("saved" in field || "value" in field)
    ) {
      return field.saved ?? field.value ?? null;
    }

    return field ?? null;
  };

  const setFieldSavedValue = (field, nextValue) => {
    if (
      field &&
      typeof field === "object" &&
      !Array.isArray(field) &&
      ("saved" in field || "value" in field)
    ) {
      return {
        ...field,
        saved: nextValue,
      };
    }

    return nextValue;
  };

  const rebuildTicketsForTimezoneChange = (
    ticketSets = [],
    fromTimeZone,
    toTimeZone
  ) => {
    if (!fromTimeZone || !toTimeZone || fromTimeZone === toTimeZone) {
      return null;
    }

    let hasTicketChanges = false;

    const rebuiltTicketSets = ticketSets.map((ticketSet) => ({
      ...ticketSet,
      tickets: (ticketSet.tickets || []).map((ticket) => {
        const updatedTicket = { ...ticket };

        TICKET_DATE_FIELDS.forEach((dateField) => {
          const rawUtc = getFieldValue(ticket[dateField]);

          if (!rawUtc) return;

          const rebuiltUtc = rebaseUtcDateToNewTimezone(
            rawUtc,
            fromTimeZone,
            toTimeZone
          );

          if (!rebuiltUtc) return;

          hasTicketChanges = true;

          updatedTicket[dateField] = setFieldSavedValue(
            ticket[dateField],
            rebuiltUtc
          );

          const timezoneField = TICKET_DATE_TIMEZONE_FIELDS[dateField];

          updatedTicket[timezoneField] = setFieldSavedValue(
            ticket[timezoneField],
            toTimeZone
          );
        });

        return updatedTicket;
      }),
    }));

    return hasTicketChanges ? rebuiltTicketSets : null;
  };

  const formatTicketsForBackend = (ticketSets = []) => {
    return ticketSets.map((ticketSet) => {
      const formattedTicketSet = {};

      Object.keys(ticketSet).forEach((key) => {
        if (
          typeof ticketSet[key] === "object" &&
          ticketSet[key] !== null &&
          !Array.isArray(ticketSet[key]) &&
          ticketSet[key].hasOwnProperty("saved")
        ) {
          formattedTicketSet[key] =
            ticketSet[key].saved !== null
              ? ticketSet[key].saved
              : ticketSet[key].value;
        } else if (key === "tickets" && Array.isArray(ticketSet[key])) {
          formattedTicketSet[key] = ticketSet[key].map((ticket) => {
            const formattedTicket = {};

            Object.keys(ticket).forEach((ticketKey) => {
              if (
                typeof ticket[ticketKey] === "object" &&
                ticket[ticketKey] !== null &&
                !Array.isArray(ticket[ticketKey]) &&
                ticket[ticketKey].hasOwnProperty("saved")
              ) {
                formattedTicket[ticketKey] =
                  ticket[ticketKey].saved !== null
                    ? ticket[ticketKey].saved
                    : ticket[ticketKey].value;
              } else {
                formattedTicket[ticketKey] = ticket[ticketKey];
              }
            });

            return formattedTicket;
          });
        } else {
          formattedTicketSet[key] = ticketSet[key];
        }
      });

      return formattedTicketSet;
    });
  };

  const handleDateChange = async (keyOrUpdates, newValue, isTime) => {
    const updates =
      typeof keyOrUpdates === "object" && keyOrUpdates !== null
        ? keyOrUpdates
        : { [keyOrUpdates]: newValue };

    const previousDates = eventInfoRef.current?.event?.dates || {};

    const changedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key, value]) => {
        const prevField = previousDates[key];
        const prevValue = prevField?.saved ?? prevField?.value ?? null;
        return prevValue !== value;
      })
    );

    if (Object.keys(changedUpdates).length === 0) {
      return;
    }

    const newDates = { ...changedUpdates };

    if (
      changedUpdates.purchaseStartDate &&
      !(
        previousDates?.startFeeDate?.saved ?? previousDates?.startFeeDate?.value
      )
    ) {
      newDates.startFeeDate = changedUpdates.purchaseStartDate;
    }

    if (
      changedUpdates.purchaseStartDate &&
      !(
        previousDates?.changeableUntilDate?.saved ??
        previousDates?.changeableUntilDate?.value
      )
    ) {
      newDates.changeableUntilDate = changedUpdates.purchaseStartDate;
    }

    const previousEventStartTimeZone =
      previousDates?.eventStartTimeZone?.saved ??
      previousDates?.eventStartTimeZone?.value ??
      "Pacific Time (PT)";

    const nextEventStartTimeZone =
      newDates?.eventStartTimeZone ?? previousEventStartTimeZone;

    const shouldRebuildSessions =
      newDates?.eventStartTimeZone &&
      newDates.eventStartTimeZone !== previousEventStartTimeZone;

    const rebuiltSessions = shouldRebuildSessions
      ? rebuildSessionsForTimezoneChange(
          eventInfoRef.current?.sessions || [],
          previousEventStartTimeZone,
          nextEventStartTimeZone
        )
      : null;

    const previousPurchaseStartTimeZone =
      previousDates?.purchaseStartTimeZone?.saved ??
      previousDates?.purchaseStartTimeZone?.value ??
      "Pacific Time (PT)";

    const nextPurchaseStartTimeZone =
      changedUpdates?.purchaseStartTimeZone ?? previousPurchaseStartTimeZone;

    const shouldRebuildTickets =
      changedUpdates?.purchaseStartTimeZone &&
      changedUpdates.purchaseStartTimeZone !== previousPurchaseStartTimeZone;

    const rebuiltTickets = shouldRebuildTickets
      ? rebuildTicketsForTimezoneChange(
          eventInfoRef.current?.tickets || [],
          previousPurchaseStartTimeZone,
          nextPurchaseStartTimeZone
        )
      : null;

    const payload = {
      event: {
        dates: changedUpdates,
      },
      ...(rebuiltSessions
        ? { sessions: formatSessionsForBackend(rebuiltSessions) }
        : {}),
      ...(rebuiltTickets
        ? { tickets: formatTicketsForBackend(rebuiltTickets) }
        : {}),
    };

    await eventEditPut.mutateAsync(payload);

    setEventInfo((prev) => ({
      ...prev,
      event: {
        ...prev.event,
        dates: {
          ...prev.event.dates,
          ...Object.fromEntries(
            Object.entries(changedUpdates).map(([key, value]) => [
              key,
              {
                ...prev.event.dates?.[key],
                saved: value,
              },
            ])
          ),
        },
      },
      ...(rebuiltSessions ? { sessions: rebuiltSessions } : {}),
      ...(rebuiltTickets ? { tickets: rebuiltTickets } : {}),
    }));

    const shouldInvalidate =
      shouldRebuildSessions ||
      shouldRebuildTickets ||
      Object.entries(changedUpdates).some(([key, value]) => {
        if (!key.endsWith("Date")) return false;
        if (!value) return false;

        const newTime = moment(value).utc().format("HH:mm");
        return isTime || newTime !== "00:00";
      });

    if (shouldInvalidate) {
      queryClient.invalidateQueries(["event", eventId]);
    }
  };

  const debouncedHandleDateChangeRef = useRef(
    _.debounce(handleDateChange, 300)
  );

  const handleDateTimeChange = (keyOrUpdates, newValue, isTime) => {
    debouncedHandleDateChangeRef.current(keyOrUpdates, newValue, isTime);
  };

  // Notes
  const handleNotesChange = (dataType, newValue) => {
    newValue = newValue.replace(/↵/g, "\r\n").trim();

    setEventInfo((prev) => {
      let currentSavedValue;
      if (dataType === "marketing.alert") {
        currentSavedValue =
          typeof prev.marketing?.alertNotes?.saved === "string"
            ? prev.marketing.alertNotes.saved.trim()
            : prev.marketing?.alertNotes?.saved;
      } else if (dataType === "marketing.info") {
        currentSavedValue =
          typeof prev.marketing?.infoNotes?.saved === "string"
            ? prev.marketing.infoNotes.saved.trim()
            : prev.marketing?.infoNotes?.saved;
      } else {
        currentSavedValue =
          typeof prev[dataType]?.notes?.saved === "string"
            ? prev[dataType].notes.saved.trim()
            : prev[dataType]?.notes?.saved;
      }

      if (newValue === currentSavedValue) {
        return prev;
      }

      let updatedInfo;
      if (dataType === "marketing.alert") {
        updatedInfo = {
          ...prev,
          marketing: {
            ...prev.marketing,
            alertNotes: {
              ...prev.marketing.alertNotes,
              saved: newValue,
            },
          },
        };
      } else if (dataType === "marketing.info") {
        updatedInfo = {
          ...prev,
          marketing: {
            ...prev.marketing,
            infoNotes: {
              ...prev.marketing.infoNotes,
              saved: newValue,
            },
          },
        };
      } else {
        updatedInfo = {
          ...prev,
          [dataType]: {
            ...prev[dataType],
            notes: {
              ...prev[dataType].notes,
              saved: newValue,
            },
          },
        };
      }

      const notesPayload =
        dataType === "marketing.alert"
          ? { marketing: { alertNotes: newValue } }
          : dataType === "marketing.info"
          ? { marketing: { infoNotes: newValue } }
          : { [dataType]: { notes: newValue } };

      debouncedHandleNotesChangeRef.current(dataType, notesPayload);
      return updatedInfo;
    });
  };

  const debouncedHandleNotesChangeRef = useRef(
    _.debounce((dataType, newNotes) => {
      eventEditPutSilent.mutateAsync(newNotes);
    }, 300)
  );

  //Venue
  const handleVenueChange = (newVenueData) => {
    debouncedEventEditPut(newVenueData);

    setEventInfo((prevEventInfo) => ({
      ...prevEventInfo,
      venue: newVenueData.venue,
    }));
  };

  const debouncedEventEditPut = useRef(
    _.debounce((newVenueData) => {
      eventEditPut.mutateAsync(newVenueData);
    }, 100)
  ).current;

  //Virtual
  const handleVirtualForm = (newValue, field, isUndoing) => {
    newValue = newValue?.trim();
    newValue = newValue === "" || newValue === undefined ? null : newValue;

    setEventInfo((prev) => ({
      ...prev,
      virtual: {
        ...prev.virtual,
        [field]: {
          ...prev.virtual[field],
          saved: newValue,
        },
      },
    }));

    const payload = {
      virtual: {
        [field]: newValue,
      },
    };

    debouncedHandleVirtualChangeRef.current(payload, isUndoing);
  };

  const debouncedHandleVirtualChangeRef = useRef(
    _.debounce((updatedEvent, isUndoing) => {
      if (isUndoing) {
        eventEditPut.mutateAsync(updatedEvent).catch((error) => {
          console.error("Backend update failed:", error);
        });
      } else {
        eventEditPutSilent.mutateAsync(updatedEvent).catch((error) => {
          console.error("Backend update failed:", error);
        });
      }
    }, 800)
  );

  // Sessions
  const handleSessions = async (newSessions, deRefetch) => {
    const formatForBackend = (data) => {
      return data.map((session) => {
        const formattedSession = {};

        Object.keys(session).forEach((key) => {
          // if (key === "id" || key === "_id") {
          //   formattedSession[key] = null;
          // } else
          if (
            typeof session[key] === "object" &&
            session[key] !== null &&
            session[key].hasOwnProperty("saved")
          ) {
            formattedSession[key] =
              session[key].saved !== null
                ? session[key].saved
                : session[key].value;
          } else {
            formattedSession[key] = session[key];
          }
        });

        return formattedSession;
      });
    };

    setEventInfo((prev) => {
      const processSessionData = (data, prevData = {}) => {
        return data.map((session) => {
          const existingSession = prevData[session.id] || {};
          const newSession = { ...session };

          Object.keys(session).forEach((key) => {
            if (
              typeof session[key] === "object" &&
              session[key] !== null &&
              session[key].hasOwnProperty("saved")
            ) {
              const prevField = existingSession[key] || {};

              newSession[key] = {
                ...session[key],
                saved:
                  session[key].saved !== null
                    ? session[key].saved
                    : session[key].value,
              };
            }
          });

          return newSession;
        });
      };

      const updatedSessionData = processSessionData(
        newSessions,
        Object.fromEntries(
          prev.sessions.map((session) => [session.id, session])
        )
      );

      const backendFormattedData = formatForBackend(updatedSessionData);

      return {
        ...prev,
        sessions: updatedSessionData,
      };
    });

    debouncedHandleSessionsRef.current(
      formatForBackend(newSessions),
      deRefetch
    );
  };

  const debouncedHandleSessionsRef = useRef(
    _.debounce((newSessions, deRefetch) => {
      if (deRefetch) {
        eventEditPut.mutateAsync({
          sessions: newSessions,
        });
      } else {
        eventEditPutSilent.mutateAsync({
          sessions: newSessions,
        });
      }
    }, 800)
  );

  const [isHostsLoading, setIsHostsLoading] = useState(false);

  //Hosts
  const handleAddHosts = async (newHosts) => {
    await eventEditPut.mutateAsync({
      hosts: newHosts,
    });

    setEventInfo((prevEventInfo) => {
      const updatedInfo = {
        ...prevEventInfo,
        hosts: {
          saved: newHosts,
          value: prevEventInfo.hosts?.value || [],
        },
      };

      return updatedInfo;
    });
    setIsHostsLoading(false);
  };

  // ADA
  const handleADAQuestions = (newEntries, doRefetch) => {
    setEventInfo((prev) => ({
      ...prev,
      ada: newEntries,
    }));

    debouncedHandleADAQuestionsRef.current(newEntries, doRefetch);
  };

  // Debounced backend call reference
  const debouncedHandleADAQuestionsRef = useRef(
    _.debounce((updatedEntries, doRefetch) => {
      // console.log("Updated ADA entries sent to backend:", updatedEntries);

      const transformedEntries = updatedEntries.map((entry) => {
        const { id, component, icon, order, param1, param2 } = entry;

        return {
          _id: id,
          component:
            component?.saved?.toLowerCase() ||
            component?.value?.toLowerCase() ||
            null,
          icon: icon?.saved || icon?.value || null,
          order: order?.saved || order?.value || null,
          param1:
            param1?.saved === "" ? "" : param1?.saved || param1?.value || null,
          param2: param2?.saved ?? param2?.value ?? null,
        };
      });

      if (doRefetch) {
        eventEditPut
          .mutateAsync({ ada: transformedEntries })
          .catch((error) =>
            console.error("Failed to update ADA entries:", error)
          );
      } else {
        eventEditPutSilent
          .mutateAsync({ ada: transformedEntries })
          .catch((error) =>
            console.error("Failed to update ADA entries:", error)
          );
      }
    }, 800)
  );

  const handleCircumstances = (newCircumstances) => {
    const newValue = newCircumstances;

    setEventInfo((prev) => {
      const currentSavedValue = prev.event?.securityNotes?.saved ?? "";

      if (newValue === currentSavedValue) {
        return prev;
      }

      const updatedInfo = {
        ...prev,
        event: {
          ...prev.event,
          securityNotes: { ...prev.event.securityNotes, saved: newValue },
        },
      };

      debouncedHandleCircumstancesChangeRef.current({
        event: { securityNotes: newValue },
      });

      return updatedInfo;
    });
  };

  const debouncedHandleCircumstancesChangeRef = useRef(
    _.debounce((updatedEvent) => {
      eventEditPutSilent.mutateAsync(updatedEvent).catch((error) => {
        console.error("Backend update failed:", error);
      });
    }, 800)
  );

  //Function handles the undo changes
  const handleUndoChangeAndUpdate = async () => {
    setLoadingUndo(true);
    let updatedChanges = {};
    const eventObjects = ["event", "marketing", "analytics", "virtual"];
    let shouldInvalidate = false;

    if (undoPath?.includes("cartType")) {
      handleUndoCartType();
    }

    if (undoPath?.includes("sessions")) {
      handleUndoSessions();
    }

    if (undoField == "virtualEventName") {
      await handleVirtualEventChange(null, null, null);
    }

    if (undoField == "virtualEventType") {
      handleVirtualForm(null, "eventType", true);
    }

    if (undoField == "virtualEventLink") {
      handleVirtualForm(null, "eventLink", true);
    }

    if (undoField == "speakers") {
      handleUndoSpeakers();
    }

    if (undoField == "limitToGroups" || undoField == "visibleTo") {
      await handleUndoVisibleTo();
    }

    if (undoField == "hosts") {
      handleUndoHosts();
    }

    if (undoField.startsWith("ada")) {
      handleUndoADA();
    }

    if (undoField == "ticketing") {
      handleUndoTickets();
    }

    if (undoField == "venue") {
      handleUndoVenue();
    }

    eventObjects.forEach((eventObject) => {
      let tempContext = {};
      if (eventInfo[eventObject]) {
        const isEventNotes = eventObject === "event" && undoField === "notes";
        const isMarketingNotes =
          eventObject === "marketing" && undoField === "notes";
        const isAlertNotes =
          eventObject === "marketing" && undoField === "marketing.alertNotes";
        const isInfoNotes =
          eventObject === "marketing" && undoField === "marketing.infoNotes";

        const proceed =
          undoField !== "notes" ||
          isEventNotes ||
          isMarketingNotes ||
          isAlertNotes ||
          isInfoNotes;

        if (proceed && undoField !== "cartType") {
          if (eventObject === "venue" && undoField === "venue") {
            // Set saved properties to current value properties for company and room
            tempContext.company = {
              ...eventInfo.venue.company,
              saved: { ...eventInfo.venue.company.value },
            };
            tempContext.room = {
              ...eventInfo.venue.room,
              saved: { ...eventInfo.venue.room.value },
            };
            updatedChanges[eventObject] = {
              // ...eventInfo[eventObject],
              ...tempContext,
            };
            shouldInvalidate = true;
          } else if (
            findAndUpdateField(eventInfo[eventObject], undoField, tempContext)
          ) {
            updatedChanges[eventObject] = {
              // ...eventInfo[eventObject],
              ...tempContext,
            };
            shouldInvalidate = true;
          }
        }
      }
    });

    if (Object.keys(updatedChanges).length > 0) {
      await eventEditPut.mutateAsync(updatedChanges, {
        onSuccess: () => {
          if (shouldInvalidate) {
            queryClient.invalidateQueries(["event", eventId]);
          }
        },
      });
    }
  };

  const findAndUpdateField = (obj, field, context) => {
    let fieldUpdated = false;

    if (field === "venue") {
      // Special handling for venue: set saved properties to the current value
      if (obj.company && obj.company.value) {
        context.company = {
          ...obj.company,
          saved: { ...obj.company.value }, // Set saved to the current value
        };
        fieldUpdated = true;
      }
      if (obj.room && obj.room.value) {
        context.room = {
          ...obj.room,
          saved: { ...obj.room.value }, // Set saved to the current value
        };
        fieldUpdated = true;
      }
    } else if (field === "notes") {
      // Existing handling for notes
      if (
        undoPath === "event.notes" &&
        obj === eventInfo.event &&
        field in obj
      ) {
        context[field] = null;
        fieldUpdated = true;
      } else if (
        undoPath === "marketing.notes" &&
        obj === eventInfo.marketing &&
        field in obj
      ) {
        context[field] = null;
        fieldUpdated = true;
      } else if (
        undoPath === "marketing.alertNotes" &&
        obj === eventInfo.marketing.infoNotes &&
        "alertNotes" in obj
      ) {
        context.alertNotes = { ...obj.alertNotes, saved: null };
        fieldUpdated = true;
      } else if (
        undoPath === "marketing.infoNotes" &&
        obj === eventInfo.marketing &&
        "infoNotes" in obj
      ) {
        context.infoNotes = { ...obj.infoNotes, saved: null };
        fieldUpdated = true;
      }
    } else if (field in obj) {
      // Handle checkboxes and other fields
      // if (
      //   field === "countingTickets" ||
      //   field === "soldOut" ||
      //   field === "tentative" ||
      //   field === "visibleCalendar"
      // ) {
      //   context[field] = { ...obj[field], saved: null };
      // } else {
      context[field] = null;
      // }
      fieldUpdated = true;
    } else {
      Object.keys(obj).forEach((key) => {
        if (obj[key] !== null && typeof obj[key] === "object") {
          let nestedContext = {};
          if (findAndUpdateField(obj[key], field, nestedContext)) {
            context[key] = { ...context[key], ...nestedContext };
            fieldUpdated = true;
          }
        }
      });
    }

    return fieldUpdated;
  };

  const handleUndoButtonClick = (
    index,
    path,
    field,
    originalValue,
    fieldDisplay
  ) => {
    setLoadingUndo(false);
    setRemoveChangesModal(true);
    setUndoIndex(index);
    setUndoField(field);
    setUndoPath(path);
    setOriginal(originalValue);
    setUndoFieldDisplay(fieldDisplay);
  };

  //Display state in words, don't display if value is "0"
  const getStateNameByAbbreviation = (abbr) => {
    if (abbr === "") return "N/A";

    const state = states?.find(
      (state) => state?.abbreviation?.toString() === abbr?.toString()
    );

    if (state) {
      return `${state.abbreviation} - ${state.state}`;
    } else {
      return "N/A";
    }
  };

  const handleViewCreatedEvent = () => {
    changeShowSavedModal(false);
    history.push(`/events/view/event/losv2/${eventId}`);
  };

  const onPublish = () => {
    eventPublish.mutateAsync().then(() => setLoadingPublish(false));
  };

  const handlePublish = () => {
    setLoadingPublish(true);
    changeShowSavedModal(true);

    onPublish();
  };

  if (isLoading && eventInfo) {
    return (
      <div className={styles.loader}>
        <div>
          <Loader />
        </div>
        <div className={styles.loaderText}>Loading Edit LOS V2.0</div>
      </div>
    );
  }

  const countryIdName = {
    US: "U.S.",
    CA: "CDN",
    AU: "ANZ",
    JM: "JAMAICA",
  };

  function transformData(array) {
    const valueArray = [];
    const savedArray = [];

    array.forEach((item) => {
      const valueObject = {};
      const savedObject = {};

      for (const key in item) {
        if (key === "tickets" && Array.isArray(item[key])) {
          const ticketValueArray = [];
          const ticketSavedArray = [];

          item[key].forEach((ticket) => {
            const ticketValueObj = {};
            const ticketSavedObj = {};

            if (ticket.id) {
              ticketValueObj.id = ticket.id;
              ticketSavedObj.id = ticket.id;
            } else if (ticket._id) {
              ticketValueObj._id = ticket._id;
              ticketSavedObj._id = ticket._id;
            }

            for (const ticketKey in ticket) {
              if (
                ticket[ticketKey] &&
                typeof ticket[ticketKey] === "object" &&
                ticket[ticketKey].hasOwnProperty("value") &&
                ticket[ticketKey].hasOwnProperty("saved")
              ) {
                ticketValueObj[ticketKey] = ticket[ticketKey].value;
                ticketSavedObj[ticketKey] = ticket[ticketKey].saved;
              }
            }

            ticketValueArray.push(ticketValueObj);
            ticketSavedArray.push(ticketSavedObj);
          });

          valueObject[key] = ticketValueArray;
          savedObject[key] = ticketSavedArray;
        } else if (
          item[key] &&
          typeof item[key] === "object" &&
          item[key].hasOwnProperty("value") &&
          item[key].hasOwnProperty("saved")
        ) {
          valueObject[key] = item[key].value;
          savedObject[key] = item[key].saved;
        } else if (key === "id") {
          valueObject.id = item[key];
          savedObject.id = item[key];
        } else if (key === "_id") {
          valueObject._id = item[key];
          savedObject._id = item[key];
        }
      }

      valueArray.push(valueObject);
      savedArray.push(savedObject);
    });

    return { valueArray: valueArray, savedArray: savedArray };
  }

  const areArraysIdentical = (array1 = [], array2 = []) => {
    if (array1?.length !== array2?.length) {
      // console.log(
      //   "Arrays have different lengths:",
      //   array1.length,
      //   array2.length
      // );
      return false;
    }

    const sortByStringifiedObject = (array) =>
      array
        .slice()
        .map((item) => {
          // Remove id and _id and keep only string properties
          const filteredItem = Object.keys(item)
            .filter(
              (key) =>
                key !== "id" && key !== "_id" && typeof item[key] === "string"
            )
            .sort()
            .reduce((obj, key) => {
              obj[key] = item[key];
              return obj;
            }, {});
          return JSON.stringify(filteredItem);
        })
        .sort();

    const sortedArray1 = sortByStringifiedObject(array1);
    const sortedArray2 = sortByStringifiedObject(array2);

    // console.log("Sorted Processed Array 1:", sortedArray1);
    // console.log("Sorted Processed Array 2:", sortedArray2);

    return sortedArray1.every((item, index) => {
      const isEqual = item === sortedArray2[index];
      // if (!isEqual) {
      //   console.log(
      //     `Mismatch found at index ${index}:`,
      //     item,
      //     sortedArray2[index]
      //   );
      // }
      return isEqual;
    });
  };

  const adaFieldChanges = eventInfo.ada
    .map((entry, index) => {
      const changes = [];

      if (entry.param1?.saved !== entry.param1?.value) {
        const param1Field = entry.param1?.field || "Param 1";
        changes.push({
          field: `${param1Field}`,
          field_name: `ada.${index}.param1`,
          path: `ada.${index}.param1`,
          original_value: entry.param1?.value || "",
          saved_value:
            entry.param1?.saved === null ? null : entry.param1?.saved ?? "",
        });
      }

      if (entry.param2?.saved !== entry.param2?.value) {
        const param2Field = entry.param2?.field || "Param 2";
        changes.push({
          field: `${param2Field}`,
          field_name: `ada.${index}.param2`,
          path: `ada.${index}.param2`,
          original_value: entry.param2?.value || "",
          saved_value:
            entry.param2?.saved === null ? null : entry.param2?.saved ?? "",
        });
      }

      if (entry.icon?.saved !== entry.icon?.value) {
        const iconField = entry.icon?.field || "ADA > Icon";

        const getFileType = (base64) => {
          if (!base64) return null;
          const match = base64.match(
            /^data:(image\/(jpeg|png|svg\+xml));base64,/
          );
          return match ? match[1] : "Unknown Type";
        };

        changes.push({
          field: `${iconField}`,
          field_name: `ada.${index}.icon`,
          path: `ada.${index}.icon`,
          original_value: getFileType(entry.icon?.value) || "",
          saved_value:
            entry.icon?.saved === null
              ? null
              : getFileType(entry.icon?.saved) ?? "",
        });
      }

      return changes;
    })
    .flat();

  const fieldChanges = [
    {
      field: "Display Name",
      field_name: "title",
      path: "marketing.title",
      original_value: eventInfo?.marketing?.title?.value,
      saved_value: eventInfo?.marketing?.title?.saved,
      field_permission: eventInfo?.marketing?.title?.permission,
    },
    {
      field: "Event Description",
      field_name: "description",
      path: "marketing.description",
      original_value: eventInfo?.marketing?.description?.value,
      saved_value: eventInfo?.marketing?.description?.saved,
      field_permission: eventInfo?.marketing?.description?.permission,
    },
    {
      field: "Speakers",
      field_name: "speakers",
      path: "speakers",
      original_value: eventInfo?.speakers?.value,
      saved_value: eventInfo?.speakers?.saved,
      field_permission: eventInfo?.speakers?.permission,
    },
    {
      field: "City",
      field_name: "city",
      path: "event.city",
      original_value: eventInfo.event.city?.value,
      saved_value: eventInfo.event.city?.saved,
      field_permission: eventInfo.event.city?.permission,
    },
    {
      field: "Country",
      field_name: "country",
      path: "event.country",
      original_value: countryIdName[eventInfo.event.country?.value],
      saved_value: countryIdName[eventInfo.event.country?.saved],
      field_permission: eventInfo.event.country?.permission,
    },
    {
      field: "State",
      field_name: "state",
      path: "event.state",
      original_value: eventInfo.event.state?.value
        ? getStateNameByAbbreviation(eventInfo.event.state?.value)
        : "",
      saved_value:
        eventInfo.event.state?.saved === null
          ? null
          : getStateNameByAbbreviation(eventInfo.event.state?.saved),
      field_permission: eventInfo.event.state?.permission,
    },
    {
      field: "Merchant Account",
      field_name: "cartType",
      path: "event.financial.cartType",
      original_value: eventInfo.event?.financial?.cartType?.value?.name,
      saved_value: eventInfo.event?.financial?.cartType?.saved?.name,
      field_permission: eventInfo.event?.financial?.cartType?.permission,
    },
    {
      field: "Visible on Calendar",
      field_name: "visibleCalendar",
      path: "marketing.visibleCalendar",
      original_value: eventInfo.marketing?.visibleCalendar?.value ? "Yes" : "",
      saved_value:
        eventInfo.marketing?.visibleCalendar?.saved === false
          ? "No"
          : eventInfo.marketing?.visibleCalendar?.saved
          ? "Yes"
          : "",
      field_permission: eventInfo.marketing?.visibleCalendar?.permission,
    },
    {
      field: "Display First Name(s)",
      field_name: "primaryCaption",
      path: "marketing.primaryCaption",
      original_value: eventInfo.marketing?.primaryCaption?.value,
      saved_value: eventInfo.marketing?.primaryCaption?.saved,
      field_permission: eventInfo.marketing?.primaryCaption?.permission,
    },
    {
      field: "Display Last Name",
      field_name: "secondaryCaption",
      path: "marketing.secondaryCaption",
      original_value: eventInfo.marketing?.secondaryCaption?.value,
      saved_value: eventInfo.marketing?.secondaryCaption?.saved,
      field_permission: eventInfo.marketing?.secondaryCaption?.permission,
    },
    {
      field: "Visible To",
      field_name: "visibleTo",
      path: "marketing.visibleTo",
      original_value: eventInfo.marketing?.visibleTo?.value,
      saved_value: eventInfo.marketing?.visibleTo?.saved,
      field_permission: eventInfo.marketing?.visibleTo?.permission,
    },
    {
      field: "Tentative",
      field_name: "tentative",
      path: "marketing.tentative",
      original_value:
        eventInfo.marketing?.tentative?.value === false
          ? "No"
          : eventInfo.marketing?.tentative?.value
          ? "Yes"
          : "",
      saved_value:
        eventInfo.marketing?.tentative?.saved === null
          ? null
          : eventInfo.marketing?.tentative?.saved
          ? "Yes"
          : "No",
      field_permission: eventInfo.marketing?.tentative?.permission,
    },
    {
      field: "Active",
      field_name: "active",
      path: "marketing.active",
      original_value:
        eventInfo.marketing?.active?.value === false
          ? "No"
          : eventInfo.marketing?.active?.value
          ? "Yes"
          : "",
      saved_value:
        eventInfo.marketing?.active?.saved === null
          ? null
          : eventInfo.marketing?.active?.saved
          ? "Yes"
          : "No",
      field_permission: eventInfo.marketing?.active?.permission,
    },
    {
      field: "Sold Out",
      field_name: "soldOut",
      path: "analytics.sales.soldOut",
      original_value: eventInfo.analytics?.sales?.soldOut?.value ? "Yes" : "",
      saved_value:
        eventInfo.analytics?.sales?.soldOut?.saved === false
          ? "No"
          : eventInfo.analytics?.sales?.soldOut?.saved
          ? "Yes"
          : "",
      field_permission: eventInfo.analytics?.sales?.soldOut?.permission,
    },
    {
      field: "Limit To Groups",
      field_name: "limitToGroups",
      path: "visibleTo",
      original_value: eventInfo?.visibleTo?.value,
      saved_value: eventInfo?.visibleTo?.saved,
      field_permission: eventInfo?.visibleTo?.permission,
    },
    {
      field: "Counting Tickets",
      field_name: "countingTickets",
      path: "analytics.countingTickets",
      original_value: eventInfo?.analytics?.countingTickets?.value ? "Yes" : "",
      saved_value:
        eventInfo?.analytics?.countingTickets?.saved === false
          ? "No"
          : eventInfo?.analytics?.countingTickets?.saved
          ? "Yes"
          : "",
      field_permission: eventInfo?.analytics?.countingTickets?.permission,
    },
    {
      field: "Purchase Capacity",
      field_name: "purchase",
      path: "analytics.sales.capacity.purchase",
      original_value: eventInfo.analytics?.sales.capacity?.purchase?.value,
      saved_value: eventInfo.analytics?.sales.capacity?.purchase?.saved,
      field_permission:
        eventInfo.analytics?.sales.capacity?.purchase?.permission,
    },
    {
      field: "Platinum Capacity",
      field_name: "platinum",
      path: "analytics.sales.capacity.platinum",
      original_value: eventInfo.analytics?.sales.capacity?.platinum?.value,
      saved_value: eventInfo.analytics?.sales.capacity?.platinum?.saved,
      field_permission:
        eventInfo.analytics?.sales.capacity?.platinum?.permission,
    },
    {
      field: "Virtual Event Type",
      field_name: "virtualEventType",
      path: "virtual.eventType",
      original_value: eventData.virtual?.eventType?.value,
      saved_value: eventData.virtual?.eventType?.saved,
      field_permission: eventData.virtual?.eventType?.permission,
    },
    {
      field: "Virtual Event Link",
      field_name: "virtualEventLink",
      path: "virtual.eventLink",
      original_value: eventData.virtual?.eventLink?.value,
      saved_value: eventData.virtual?.eventLink?.saved,
      field_permission: eventData.virtual?.eventLink?.permission,
    },
    {
      field: "Virtual Event Name",
      field_name: "virtualEventName",
      path: "virtual.eventName",
      original_value: eventData.virtual?.linkedEvent?.value?.name,
      saved_value: eventData.virtual?.linkedEvent?.saved?.name,
      field_permission: eventData.virtual?.linkedEvent?.permission,
    },
    {
      field: "Event Venue",
      field_name: "venue",
      path: "venue",
      original_value: `${eventInfo?.venue?.company?.value?.venueName || ""}${
        eventInfo?.venue?.room?.value?.name
          ? ` (${eventInfo?.venue?.room?.value?.name})`
          : ""
      }`,
      saved_value: `${
        eventInfo?.venue?.company?.value?.netSuiteId &&
        eventInfo?.venue?.company?.saved?.noVenue &&
        !eventInfo?.venue?.company?.saved?.netSuiteId
          ? "No Event Venue"
          : `${eventInfo?.venue?.company?.saved?.venueName || ""}${
              eventInfo?.venue?.room?.saved?.name
                ? ` (${eventInfo?.venue?.room?.saved?.name})`
                : ""
            }`
      }`,
      field_permission: eventInfo?.venue?.company?.permission,
    },
    {
      field: "Sessions",
      field_name: "sessions",
      path: "sessions",
      original_value: transformData(eventInfo.sessions).valueArray,
      saved_value: transformData(eventInfo.sessions).savedArray,
      field_permission: eventInfo.sessions?.permission,
    },
    {
      field: "Hosts",
      field_name: "hosts",
      path: "hosts",
      original_value: eventInfo?.hosts?.value,
      saved_value: eventInfo?.hosts?.saved,
      field_permission: eventInfo?.hosts?.permission,
    },
    {
      field: "Security Notes",
      field_name: "securityNotes",
      path: "event.securityNotes",
      original_value: eventInfo.event?.securityNotes?.value
        ? removeExtraLineSpaces(
            decodeURI(eventInfo.event?.securityNotes?.value)
          )
        : "",
      saved_value:
        eventInfo.event?.securityNotes?.saved === null
          ? null
          : removeExtraLineSpaces(
              decodeURI(eventInfo.event?.securityNotes?.saved || "")
            ),
      field_permission: eventInfo.event?.securityNotes?.permission,
    },
    {
      field: "Internal Notes",
      field_name: "notes",
      path: "event.notes",
      original_value: eventInfo.event?.notes?.value
        ? removeExtraLineSpaces(decodeURI(eventInfo.event?.notes?.value))
        : "",
      saved_value:
        eventInfo.event?.notes?.saved === null
          ? null
          : removeExtraLineSpaces(
              decodeURI(eventInfo.event?.notes?.saved || "")
            ),
      field_permission: eventInfo.event?.notes?.permission,
    },
    {
      field: "Calendar Notes",
      field_name: "notes",
      path: "marketing.notes",
      original_value: eventInfo.marketing?.notes?.value
        ? removeExtraLineSpaces(decodeURI(eventInfo.marketing?.notes?.value))
        : "",
      saved_value:
        eventInfo.marketing?.notes?.saved === null
          ? null
          : removeExtraLineSpaces(
              decodeURI(eventInfo.marketing?.notes?.saved || "")
            ),
      field_permission: eventInfo.marketing?.notes?.permission,
    },
    {
      field: "Alert Notes",
      field_name: "alertNotes",
      path: "marketing.alertNotes",
      original_value: eventInfo.marketing?.alertNotes?.value
        ? removeExtraLineSpaces(
            decodeURI(eventInfo.marketing?.alertNotes?.value)
          )
        : "",
      saved_value:
        eventInfo.marketing?.alertNotes?.saved === null
          ? null
          : removeExtraLineSpaces(
              decodeURI(eventInfo.marketing?.alertNotes?.saved || "")
            ),
      field_permission: eventInfo.marketing?.alertNotes?.permission,
    },
    {
      field: "Info Notes",
      field_name: "infoNotes",
      path: "marketing.infoNotes",
      original_value: eventInfo.marketing?.infoNotes?.value
        ? removeExtraLineSpaces(
            decodeURI(eventInfo.marketing?.infoNotes?.value)
          )
        : "",
      saved_value:
        eventInfo.marketing?.infoNotes?.saved === null
          ? null
          : removeExtraLineSpaces(
              decodeURI(eventInfo.marketing?.infoNotes?.saved || "")
            ),
      field_permission: eventInfo.marketing?.infoNotes?.permission,
    },
    {
      field: "Ticketing",
      field_name: "ticketing",
      path: "ticketing",
      original_value: transformData(eventInfo.tickets).valueArray,
      saved_value: transformData(eventInfo.tickets).savedArray,
      field_permission: eventInfo.tickets?.permission,
    },
    ...adaFieldChanges,
  ].filter((change) => {
    if (
      [
        "countingTickets",
        "soldOut",
        "tentative",
        "active",
        "visibleCalendar",
      ].includes(change.field_name)
    ) {
      const isOriginalUnselected =
        !change.original_value || change.original_value === "No";
      const isSavedNo = change.saved_value === "No";
      const isSavedYes = change.saved_value === "Yes";

      return (
        !(isOriginalUnselected && isSavedNo) &&
        !(!isOriginalUnselected && isSavedYes)
      );
    }

    if (change.field_name === "sessions") {
      let hasChanges = false;
      change.saved_value.map((savedSession, index) => {
        Object.keys(savedSession).map((key) => {
          if (!Array.isArray(savedSession[key])) {
            if (savedSession[key] !== change.original_value[index][key]) {
              hasChanges = true;
            }
          }
        });
      });
      return hasChanges;
    } else if (change.field_name === "ticketing") {
      let hasChanges = false;
      change.saved_value.map((savedTicketSet, index) => {
        const originalTicketSet = change.original_value.find(
          (tktSet) => tktSet.id == savedTicketSet.id
        );
        Object.keys(savedTicketSet).map((key) => {
          if (!Array.isArray(originalTicketSet[key])) {
            if (
              savedTicketSet[key] &&
              savedTicketSet[key] !== originalTicketSet[key]
            ) {
              hasChanges = true;
            }
          } else {
            if (key == "tickets") {
              savedTicketSet.tickets.map((savedTicket) => {
                const originalTicket = originalTicketSet.tickets.find(
                  (tkt) => tkt.id == savedTicket.id
                );
                Object.keys(savedTicket).map((key) => {
                  if (!Array.isArray(originalTicket[key])) {
                    if (
                      savedTicket[key] &&
                      originalTicket &&
                      savedTicket[key] !== originalTicket[key]
                    ) {
                      hasChanges = true;
                    }
                  } else {
                    if (
                      savedTicket[key] &&
                      !areArraysIdentical(savedTicket[key], originalTicket[key])
                    ) {
                      hasChanges = true;
                    }
                  }
                });
              });
            } else {
              if (
                savedTicketSet[key] &&
                !areArraysIdentical(
                  savedTicketSet[key] !== originalTicketSet[key]
                )
              ) {
                hasChanges = true;
              }
            }
          }
        });
      });
      return hasChanges;
    } else if (
      change.field_name === "limitToGroups" ||
      change.field_name == "hosts" ||
      change.field_name === "speakers"
    ) {
      if (change.saved_value == null) {
        return false;
      } else if (
        areArraysIdentical(change.saved_value, change.original_value)
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (
        (Array.isArray(change.saved_value) &&
          change.saved_value.length === 0) ||
        (Array.isArray(change.original_value) &&
          Array.isArray(change.saved_value) &&
          change.field_name !== "ticketing" &&
          change.original_value.length == change.saved_value.length)
      ) {
        return false;
      }
    }

    return true;
  });

  const capitalizeFirstLetter = (string) => {
    if (!string) return "";

    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const datesUndoChanges = (eventInfo) => {
    const dateFields = eventInfo?.event?.dates || {};

    const undoChanges = Object.entries(dateFields).reduce((acc, [key, obj]) => {
      const isDateOrTimezone = key.endsWith("Date") || key.endsWith("TimeZone");

      if (!isDateOrTimezone) {
        return acc;
      }

      if (!obj || obj.saved == null) {
        return acc;
      }

      const fallbackFieldName = key
        .replace("StartDate", " Begin")
        .replace("EndDate", " End")
        .replace("StartTimeZone", " Start Timezone")
        .replace("EndTimeZone", " End Timezone")
        .replace("TimeZone", " Timezone")
        .replace("Date", "")
        .replace(/([A-Z])/g, " $1")
        .trim();

      const displayField =
        typeof obj.field === "string" && obj.field.includes("> ")
          ? obj.field.split("> ")[1]
          : fallbackFieldName;

      acc.push({
        field: displayField,
        field_name: key,
        original_value: obj.value,
        saved_value: obj.saved,
      });

      return acc;
    }, []);

    return undoChanges;
  };

  const dateChanges = datesUndoChanges(eventInfo);

  function hasMeaningfulChange(saved, original) {
    if (Array.isArray(saved)) {
      if (!Array.isArray(original)) return hasTruthyValue(saved); // can't compare
      if (saved.length !== original.length) return true;
      return saved.some((val, i) => hasMeaningfulChange(val, original[i]));
    }

    if (saved && typeof saved === "object") {
      if (!original || typeof original !== "object")
        return hasTruthyValue(saved);
      return Object.entries(saved).some(([key, val]) => {
        if (key === "id" || key === "_id") return false;
        return hasMeaningfulChange(val, original[key]);
      });
    }

    return Boolean(saved) && saved !== original;
  }

  function hasTruthyValue(value) {
    if (Array.isArray(value)) {
      return value.some(hasTruthyValue);
    }

    if (value && typeof value === "object") {
      return Object.entries(value).some(([key, val]) => {
        if (key === "id" || key === "_id") return false;
        return hasTruthyValue(val);
      });
    }

    return Boolean(value);
  }

  setTimeout(() => {
    const allChanges = [...fieldChanges, ...dateChanges];

    const unsavedChanges = allChanges.filter(
      ({ saved_value, original_value }) =>
        hasMeaningfulChange(saved_value, original_value)
    );

    const unsavedChangesExist = unsavedChanges.length > 0;

    setShowUndoButton(unsavedChangesExist);
  }, 100);

  //Handles back button
  const handleBackClick = () => {
    // Trigger validation
    setTriggerValidation((prev) => !prev);

    setTimeout(() => {
      if (isFormValid && hasUnsavedChanges) {
        queryClient.invalidateQueries(QUERY_KEYS.events("25"));
        queryClient.invalidateQueries(QUERY_KEYS.events("50"));
        queryClient.invalidateQueries(QUERY_KEYS.events("all"));
        queryClient.invalidateQueries(QUERY_KEYS.archivedEvents("25"));
        queryClient.invalidateQueries(QUERY_KEYS.archivedEvents("50"));
        queryClient.invalidateQueries(QUERY_KEYS.archivedEvents("all"));
        history.push("/events/events-details", { refetch: true });
        refetch();
      }
    }, 0);
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div>
          <span className={styles.backButton} onClick={handleBackClick}>
            {"< Back"}
          </span>
          <h1 className={styles.title}>EDIT EVENT - LOS V2.0</h1>
        </div>

        <div className={styles.buttonContainer}>
          {showUndoButton && (
            <Button
              className={styles.undoButton}
              children={"UNDO CHANGES"}
              onClick={() => setUndoChangesModal(true)}
            />
          )}
          {loadingPublish ? (
            <Loader />
          ) : (
            <Button
              className={classNames(
                isPublishing || eventInfo?.event?.status?.value == "Publishing"
                  ? styles.greenButton
                  : styles.saveButton,
                {
                  [styles.disabledButton]:
                    !enablePublishButton || isEditLoading,
                }
              )}
              onClick={() => {
                if (!enablePublishButton || isEditLoading) {
                  !isEditLoading && setIsMissingFieldsModalOpen(true);
                  // console.log("Missing Fields: ", missingFields);
                } else {
                  handlePublish();
                }
              }}
              children={
                isPublishing || eventInfo?.event?.status?.value == "Publishing"
                  ? "PUBLISHING"
                  : isPublishing ||
                    eventInfo?.event?.status?.value == "Published"
                  ? "REPUBLISH"
                  : "PUBLISH"
              }
            />
          )}
        </div>
      </div>

      <div className={styles.bodyContainer}>
        <EventTypeV2Card
          eventInfo={eventInfo?.event}
          onChange={handleEventTypeLocation}
        />

        <div className={styles.cardContainer}>
          {eventInfo.event?.name?.saved == "Summer Conference" ||
            (eventInfo.event?.name?.value == "Summer Conference" && (
              <HostsV2Form
                hosts={eventInfo.hosts?.saved || eventInfo.hosts?.value}
                setHosts={(newHosts) => handleAddHosts(newHosts)}
                isReunion={true}
                isLoading={isHostsLoading}
                setIsLoading={setIsHostsLoading}
                isSummerConference={true}
              />
            ))}
        </div>

        <div className={styles.cardContainer}>
          {eventInfo && (
            <>
              <EventV2InfoForm
                marketingInfo={eventInfo}
                onBlur={handleMarketingOnBlur}
                pageMode="edit"
                triggerValidation={triggerValidation}
                onDateTimeChange={handleDateTimeChange}
                section="Event Information"
                onSpeakerChange={(newSpeakers) =>
                  handleSpeakerChange(newSpeakers)
                }
                onVirtualEventChange={(
                  virtualId,
                  virtualEventId,
                  virtualEventName
                ) =>
                  handleVirtualEventChange(
                    virtualId,
                    virtualEventId,
                    virtualEventName
                  )
                }
                onCartTypeChange={(name, cartTypeId) =>
                  handleCartTypeChange(name, cartTypeId)
                }
                isPublished={
                  isPublishing ||
                  eventInfo?.event?.status?.value == "Published" ||
                  eventInfo?.event?.status?.value == "Publishing"
                }
              />
            </>
          )}
        </div>
        <div className={styles.cardContainer}>
          {eventInfo && (
            <PublishingV2InfoForm
              publishingInfo={eventInfo}
              onPublishChange={handlePublishChange}
              pageMode="edit"
              onDateTimeChange={handleDateTimeChange}
              section="Publishing Information"
              onGroupChange={(newGroupsArray) =>
                handleGroupChange(newGroupsArray)
              }
              onCaptionChange={handleCaptionsChange}
            />
          )}
        </div>

        {eventInfo?.event?.location?.value === "onsite" ? (
          <div className={styles.cardContainer}>
            <VenueV2Form
              venueInfo={eventInfo}
              onVenueChange={handleVenueChange}
              pageMode="edit"
            />
          </div>
        ) : (
          <div className={styles.cardContainer}>
            <VirtualV2Form
              type={
                eventInfo.virtual?.eventType?.saved ||
                eventInfo.virtual?.eventType?.saved
              }
              link={
                eventInfo.virtual?.eventLink?.saved ||
                eventInfo.virtual?.eventLink?.value
              }
              onVirtualChange={handleVirtualForm}
            />
          </div>
        )}

        {eventInfo?.event?.location?.value === "onsite" ? (
          <div className={styles.cardContainer}>
            {eventInfo && (
              <SessionsV2Form
                sessions={eventInfo.sessions}
                setSessions={handleSessions}
                eventData={eventData}
                pageMode="edit"
              />
            )}
          </div>
        ) : (
          ""
        )}

        <div className={styles.cardContainer}>
          <TicketingV2Form
            ticketSets={eventInfo.tickets}
            setTicketSets={(newTicketData, isAddingNew, removalSet) =>
              handleTicketingForm(newTicketData, isAddingNew, removalSet)
            }
            clearSessionsTicketSet={(ticketSetId) =>
              handleClearSessionsTicketSet(ticketSetId)
            }
            isSummerConference={
              eventInfo.event?.name?.saved == "Summer Conference" ||
              eventInfo.event?.name?.value == "Summer Conference"
            }
            isPublished={
              isPublishing ||
              eventInfo?.event?.status?.value == "Published" ||
              eventInfo?.event?.status?.value == "Publishing"
            }
            isVirtual={
              (eventInfo.event?.location?.saved ||
                eventInfo.event?.location?.value) == "virtual"
            }
            eventName={
              eventInfo.event?.name?.saved || eventInfo.event?.name?.value
            }
            marketingInfo={eventInfo}
          />
        </div>

        {eventInfo?.event?.location?.value === "onsite" ? (
          <div className={styles.cardContainer}>
            {eventInfo && (
              <ADAV2Form
                entries={eventInfo.ada || []}
                setEntries={(newEntries, doRefetch) =>
                  handleADAQuestions(newEntries, doRefetch)
                }
              />
            )}
          </div>
        ) : (
          ""
        )}

        {eventInfo?.event?.location?.value === "onsite" ? (
          <div className={styles.cardContainer}>
            {eventInfo && (
              <CircumstancesV2Form
                circumstanceNotes={eventInfo.event?.securityNotes}
                setCircumstanceNotes={handleCircumstances}
              />
            )}
          </div>
        ) : (
          ""
        )}

        <div className={styles.cardContainer}>
          {eventInfo && (
            <NotesV2Form
              notesData={eventInfo}
              onNotesChange={handleNotesChange}
              pageMode="edit"
            />
          )}
        </div>
      </div>
      <div className={styles.footerContainer}>
        {loadingPublish ? (
          <Loader />
        ) : (
          <Button
            className={classNames(
              isPublishing || eventInfo?.event?.status?.value == "Publishing"
                ? styles.greenButton
                : styles.saveButton,
              {
                [styles.disabledButton]: !enablePublishButton || isEditLoading,
              }
            )}
            onClick={() => {
              if (!enablePublishButton || isEditLoading) {
                !isEditLoading && setIsMissingFieldsModalOpen(true);
                // console.log("Missing Fields: ", missingFields);
              } else {
                handlePublish();
              }
            }}
            children={
              isPublishing || eventInfo?.event?.status?.value == "Publishing"
                ? "PUBLISHING"
                : isPublishing || eventInfo?.event?.status?.value == "Published"
                ? "REPUBLISH"
                : "PUBLISH"
            }
          />
        )}
      </div>

      <Modal
        title={"Missing Required Fields"}
        isOpen={isMissingFieldsmodalOpen}
        onClose={() => setIsMissingFieldsModalOpen(false)}
        children={
          <>
            <div className={styles.missingFieldsModal}>
              <div className={styles.missingFieldsList}>
                {missingFields.map((field, index) => (
                  <div
                    className={
                      index !== missingFields.length - 1
                        ? styles.missingField
                        : styles.missingFieldLast
                    }
                  >
                    <div className={styles.name}>{field}</div>
                  </div>
                ))}
              </div>

              <div className={styles.button}>
                <button
                  className={styles.closeBtn}
                  onClick={() => setIsMissingFieldsModalOpen(false)}
                >
                  CLOSE
                </button>
              </div>
            </div>
          </>
        }
      />

      <UndoChangesModal
        isOpen={undoChangesModal}
        onCollapse={() => setUndoChangesModal(false)}
        editedEvent={[...fieldChanges, ...dateChanges].filter(
          (change) =>
            change.saved_value !== null && change.saved_value !== undefined
        )}
        onClick={handleUndoButtonClick}
      />

      {isPublishing && (
        <EventPublishModal
          isOpen={showSavedModal}
          onCollapse={() => changeShowSavedModal(false)}
          onClick={() => history.push("/events/events-details")}
          onView={() => handleViewCreatedEvent()}
        />
      )}

      <DeleteModal
        header={"REMOVE HOST"}
        description={
          "Confirm that you would like to delete [Venue Name]. This action cannot be undone."
        }
        isOpen={showRemoveModal}
        onCollapse={() => changeShowRemoveModal(false)}
        onClick={() => console.log("Removed")}
        btnName={"REMOVE"}
      />

      <DeleteModal
        header={"REMOVE CHANGES"}
        description={`Are you sure you want to undo the changes made to ${formatFieldName(
          undoFieldDisplay
        )}?`}
        isOpen={removeChangesModal}
        onCollapse={() => setRemoveChangesModal(false)}
        onClick={handleUndoChangeAndUpdate}
        btnName={"REMOVE"}
        isLoading={loadingUndo}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        errorMessage={errorModal.message}
        onClose={closeErrorModal}
      />
    </div>
  );
}
